import React, { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { PriceCard } from './components/PriceCard';
import { LuxuryGrid } from './components/LuxuryGrid';
import { Cart } from './components/Cart';
import { CartBar } from './components/CartBar';
import { luxuryItems } from './data/luxuryItems';
import { calculateGoldAmount, getGoldPrice } from './utils/conversions';
import { CartProvider } from './context/CartContext';
import Footer from './components/Footer';

interface BitcoinPriceData {
  price: number;
  timestamp: number;
  error?: string;
}

function App() {
  const [bitcoinPrice, setBitcoinPrice] = useState<number | null>(null);
  const [goldPricePerGram, setGoldPricePerGram] = useState<number | null>(null);
  const [goldLastUpdated, setGoldLastUpdated] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Define polling constants
  const POLL_INTERVAL = 45000; // 45 seconds base interval
  const JITTER = 2000;        // 2 seconds random jitter

  // Define fetchBitcoinPrice outside useEffect to make it accessible
  const fetchBitcoinPrice = async () => {
    try {
      const response = await fetch('https://bitcoin-price-api.zaidazmi27.workers.dev');
      
      if (!response.ok) {
        throw new Error('Failed to fetch Bitcoin price');
      }
      const data = await response.json() as BitcoinPriceData;
      
      if (data.error) {
        console.warn('Using fallback Bitcoin price:', data.error);
      }
      
      setBitcoinPrice(data.price);
    } catch (err) {
      console.error('Error fetching Bitcoin price:', err);
      setError('Failed to fetch Bitcoin price. Please try again later.');
    }
  };

  // Fetch Bitcoin price
  useEffect(() => {
    const fetchPrices = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchBitcoinPrice(),
          updateGoldPrice()
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchPrices();

    // Set up interval with jitter
    const interval = setInterval(() => {
      const jitterDelay = Math.random() * JITTER;
      setTimeout(fetchPrices, jitterDelay);
    }, POLL_INTERVAL);
    
    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);

  // Add event listener for page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Only refresh prices when page becomes visible
        const fetchPrices = async () => {
          setIsLoading(true);
          try {
            await Promise.all([
              fetchBitcoinPrice(),
              updateGoldPrice()
            ]);
          } finally {
            setIsLoading(false);
          }
        };
        fetchPrices();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Add event listener for manual page refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear cart data from memory before page refresh
      localStorage.removeItem('cart-state');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Fetch Gold price
  const updateGoldPrice = async () => {
    try {
      const data = await getGoldPrice();
      setGoldPricePerGram(data.price);
      setGoldLastUpdated(data.timestamp);
    } catch (err) {
      console.error('Error fetching gold price:', err);
      setError('Failed to fetch gold price. Please try again later.');
    }
  };

  const goldAmount = bitcoinPrice && goldPricePerGram && goldLastUpdated
    ? calculateGoldAmount(bitcoinPrice, goldPricePerGram, goldLastUpdated)
    : null;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Error</h2>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <CartProvider>
      <div className="relative min-h-screen">
        <div className="min-h-screen bg-fixed bg-gradient-to-br from-orange-50 to-orange-100">
          <div className="container mx-auto px-4 py-12 content-visibility-auto">
            <Header />
            
            <div className="grid grid-cols-1 gap-8 max-w-5xl mx-auto mb-16">
              <PriceCard 
                bitcoinPrice={bitcoinPrice}
                goldAmount={goldAmount}
                goldPricePerGram={goldPricePerGram}
                goldLastUpdated={goldLastUpdated}
                isLoading={isLoading}
              />
            </div>

            <LuxuryGrid 
              items={luxuryItems} 
              bitcoinPrice={bitcoinPrice || 0}
            />

            <Footer />
          </div>
        </div>

        {/* Cart Components */}
        <Cart bitcoinPrice={bitcoinPrice || 0} />
        <CartBar bitcoinPrice={bitcoinPrice || 0} />
      </div>
    </CartProvider>
  );
}

export default App;